import Dollars from "./Dollars";

/*
  TransactionsTable component.

  Columns is an array of strings that are keys of the transaction object.
  if Columns includes "description", it will be displayed in a single
  row with the other columns. Otherwise, it will be displayed in a second
  row.

  if onClickDate and onClickAmount are provided, the date and amount columns
  will be clickable to sort the table by those columns. We use callbacks rather
  than sorting the transactions directly because an accurate table will need to
  reissue another query to the database.
  
  Transaction tables always show date first and amount last.

  if with_cents is true, the amount will be displayed with cents. By default we round
  to the nearest dollar.
 */
export default function TransactionsTable({
                                    transactions,
                                    columns,
                                    withCents,
                                    onClickDate,
                                    onClickAmount,
                                    onClickTransaction,
                                    sortColumn,
                                    withTotal,
                                  }) {

  columns = columns || ['account_name', 'payee_name', 'category']
  const column_headers = {
    account_name: "Account",
    payee_name: "Payee",
    category: "Category",
    transaction_type: "Type",
    description: "Description"
  }

  // validate the column names
  columns.forEach(column => {
    if (!column_headers[column]) {
      throw new Error(`Invalid column name: ${column}`)
    }
  });

  const clickClass = onClickDate ? "cursor-pointer" : "";
  const total = transactions.reduce((acc, tx) => acc + tx.amount, 0)

  return (
    <table className={"table table-sm"}>
      <thead>
      <tr>
        <th className={clickClass}
            onClick={onClickDate}>
          Date
          {sortColumn === "date" && <span className={"ms-1"}>▼</span>}
        </th>
        {columns.map((column, index) => {
          return <th key={index}>
            {column_headers[column]}
          </th>
        })}
        <th className={`${clickClass} text-end`}
            onClick={onClickAmount}>
          Amount
          {sortColumn === "amount" && <span className={"ms-1"}>▼</span>}
        </th>
      </tr>
      </thead>
      <tbody>
      {transactions.map((tx, i) => (
        <TransactionRow key={tx.id}
                        transaction={tx}
                        isEven={i % 2 === 0}
                        onClick={onClickTransaction ? () => onClickTransaction(tx) : null}
                        withCents={withCents}
                        columns={columns}/>
      ))}
      {withTotal &&
        <tr className="table-primary">
          <th>Total</th>
          <td colSpan={columns.length + 2} className={"text-end"}>
            <Dollars amount={total} withCents={withCents}/>
          </td>
        </tr>
      }
      </tbody>
    </table>
  )
}

// if columns includes "description", it will be displayed in a single row with the other columns.
// Otherwise, we will render 2 rows, one for the transaction and one for the description.
// TODO FIXME onClick needs to be null if not provided to TransactionsTable
function TransactionRow({transaction, isEven, columns, onClick, withCents}) {
  let bg = isEven ? "" : "table-light";
  if (onClick) {
    bg = "cursor-pointer " + bg;
  }
  const {date, description, amount} = transaction;
  columns = columns || ['account_name', 'payee_name', 'category']

  const handleClick = onClick && (() => {
    onClick(transaction)
  })

  // single row
  if (columns.includes("description")) {
    return <tr className={bg} onClick={handleClick}>
      <td className={"text-nowrap"}>{date}</td>
      {columns.map((column, index) => (
        <td key={index}>{transaction[column]}</td>
      ))}
      <td className={"text-end"}>
        <Dollars amount={amount} withCents={withCents}/>
      </td>
    </tr>

    // double row
  } else {
    let indentColumn = columns.length > 1
    let colSpan = indentColumn ? columns.length + 1 : columns.length + 2


    return <>
      <tr className={bg} onClick={handleClick}>
        <td className={"text-nowrap"}>{date}</td>
        {columns.map((column, index) => <td key={index}>{transaction[column]}</td>)}
        <td className={"text-end"}><Dollars amount={amount} withCents/></td>
      </tr>
      <tr className={bg} onClick={handleClick}>
        {indentColumn && <td/>}
        <td className="text-muted" colSpan={colSpan}>{description}</td>
      </tr>
    </>
  }
}
